import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import { api } from 'src/common/utils/api';

export type LifeEvent = {
  id: string;
  /** Shared by the one-row-per-member records of an event entered once for several members. */
  groupId?: string | null;
  description: string;
  cost: number | null;
  details: string | null;
  eventDate: string | null;
  clientId: string;
};

type LifeEventInput = Omit<LifeEvent, 'id' | 'clientId'> & { id?: string };

type UseLifeEventsReturnType = {
  isLoading: boolean;
  lifeEventsData: LifeEvent[] | undefined;
  upsertLifeEvents: (items: LifeEventInput[]) => Promise<void>;
  deleteLifeEvents: (ids: string[]) => Promise<void>;
};

export default function useLifeEvents(): UseLifeEventsReturnType {
  const { clientId } = useCurrentClient();
  const queryClient = useQueryClient();

  const queryKey = ['clients', clientId, 'life-events'];

  const { data, isLoading } = useQuery<LifeEvent[]>({
    queryKey,
  });

  const { mutateAsync: upsertMutation } = useMutation({
    mutationFn: (items: LifeEventInput[]) =>
      api.put(`clients/${clientId}/life-events/batch`, items),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const upsertLifeEvents = async (items: LifeEventInput[]) => {
    await upsertMutation(items);
  };

  const { mutateAsync: deleteMutation } = useMutation({
    mutationFn: (ids: string[]) => api.post(`clients/${clientId}/life-events/delete-request`, ids),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const deleteLifeEvents = async (ids: string[]) => {
    await deleteMutation(ids);
  };

  return {
    isLoading,
    lifeEventsData: data,
    upsertLifeEvents,
    deleteLifeEvents,
  };
}
